/**
 * The fixed 256-colour palette (spec §2.1, §10).
 *
 * Index 0 is "unpainted" (transparent). The remaining 255 entries follow the
 * proposal in §10 of the spec:
 *   1–15    greys, black → white
 *   16–31   skin tones
 *   32–95   32 hues × {vivid, pastel}
 *   96–255  20 hues × 8-step tinted ramps (dark → light)
 *
 * The palette is generated deterministically so server, web and native
 * clients agree byte-for-byte; it is also served at GET /v1/palette.
 */

export type RGB = readonly [number, number, number];

function hslToRgb(h: number, s: number, l: number): RGB {
  const hh = (((h % 360) + 360) % 360) / 360;
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const f = (t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [Math.round(f(hh + 1 / 3) * 255), Math.round(f(hh) * 255), Math.round(f(hh - 1 / 3) * 255)];
}

function buildPalette(): RGB[] {
  const out: RGB[] = [[0, 0, 0]]; // 0: unpainted
  // 1–15 greys
  for (let k = 0; k < 15; k++) {
    const v = Math.round((255 * k) / 14);
    out.push([v, v, v]);
  }
  // 16–31 skin tones: warm hue, lightness from deep to pale.
  for (let k = 0; k < 16; k++) {
    const t = k / 15;
    const h = 22 + 8 * t;
    const s = 0.62 - 0.28 * t;
    const l = 0.16 + 0.76 * t;
    out.push(hslToRgb(h, s, l));
  }
  // 32–95: 32 hues, vivid then pastel.
  for (let k = 0; k < 32; k++) {
    const h = (360 * k) / 32;
    out.push(hslToRgb(h, 1, 0.5));
    out.push(hslToRgb(h, 0.9, 0.78));
  }
  // 96–255: 20 hues × 8 shades.
  for (let k = 0; k < 20; k++) {
    const h = (360 * k) / 20 + 9;
    for (let s = 0; s < 8; s++) {
      const l = 0.12 + (0.78 * s) / 7;
      out.push(hslToRgb(h, 0.7, l));
    }
  }
  if (out.length !== 256) throw new Error(`palette has ${out.length} entries`);
  return out;
}

export const PALETTE: readonly RGB[] = buildPalette();

/** 256 × RGB bytes, PLTE-chunk order. */
export const PALETTE_RGB: Uint8Array = (() => {
  const a = new Uint8Array(768);
  PALETTE.forEach((c, i) => {
    a[i * 3] = c[0];
    a[i * 3 + 1] = c[1];
    a[i * 3 + 2] = c[2];
  });
  return a;
})();

/** 256 × RGBA bytes; index 0 fully transparent. */
export const PALETTE_RGBA: Uint8Array = (() => {
  const a = new Uint8Array(1024);
  PALETTE.forEach((c, i) => {
    a[i * 4] = c[0];
    a[i * 4 + 1] = c[1];
    a[i * 4 + 2] = c[2];
    a[i * 4 + 3] = i === 0 ? 0 : 255;
  });
  return a;
})();

/** Packed ABGR (little-endian RGBA) 32-bit values for fast canvas fills. */
export const PALETTE_U32: Uint32Array = (() => {
  const a = new Uint32Array(256);
  PALETTE.forEach((c, i) => {
    const alpha = i === 0 ? 0 : 255;
    a[i] = ((alpha << 24) | (c[2] << 16) | (c[1] << 8) | c[0]) >>> 0;
  });
  return a;
})();

export function paletteHex(index: number): string {
  const c = PALETTE[index & 0xff];
  return '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');
}

export function isColourIndex(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 255;
}

/** Nearest palette index (excluding 0) to an RGB triple. */
export function nearestPaletteIndex(r: number, g: number, b: number): number {
  let best = 1;
  let bestD = Infinity;
  for (let i = 1; i < 256; i++) {
    const c = PALETTE[i];
    const d = (c[0] - r) ** 2 + (c[1] - g) ** 2 + (c[2] - b) ** 2;
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}
